import React, { useContext } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'

import InlineGroup from 'components/Common/InlineGroup'
import Pill from 'components/Luxkit/Pill'
import config from 'constants/config'
import { formatOccupantsShortTranslated } from 'lib/occupancyUtils'
import FormatCurrency from 'components/Common/FormatCurrency'
import useFlightPrice from 'hooks/useFlightPrice'
import TaxesAndFees from 'components/OfferPage/PackageOption/Pricing/TaxesAndFees'
import { connect } from 'react-redux'
import useHotelPriceColour from 'hooks/useHotelPriceColour'
import GeoContext from 'contexts/geoContext'
import TextButton from 'components/Luxkit/Button/TextButton'
import PriceDetailsModal from 'components/Common/PriceDetails/PriceDetailsModal'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Heading from 'components/Luxkit/Typography/Heading'
import Caption from 'components/Luxkit/Typography/Caption'
import { buildSuggestedDatesParamsKey, buildSuggestedDatesStringTranslated } from 'lib/searchUtils'
import { getSuggestedDates } from 'selectors/offerSelectors'
import BusinessTravellerOfferCreditsTextLink from 'businessTraveller/components/offer-credits/BusinessTravellerOfferCreditsTextLink'
import useShowNewHotelSearch from 'components/SearchV2/Hooks/useShowNewHotelSearch'
import cn from 'clsx'
import { Trans, useTranslation } from 'react-i18next'
import PaletteProvider from 'contexts/PaletteProvider'

const Root = styled.div`
  display: flex;
  flex-direction: column;
  text-align: left;
  align-items: flex-start;

  &.upsell {
    align-items: flex-start;
  }
  &.newTile {
    align-items: flex-start;
    align-self: flex-start;
  }
`

const StyledPill = styled(Pill)`
  margin: ${rem(4)} 0 0 ${rem(4)};
`

const UpsellCTAButton = styled(TextButton)`
  align-self: flex-end;
`

interface MappedStateProps {
  suggestedDates: App.OfferSuggestedDates,
}

interface Props {
  rate: App.BedbankRate;
  rooms: Array<App.Occupants>;
  offer: App.BedbankOffer | App.BedbankOfferSummary;
  filters?: App.OfferListFilters;
  offerUrl: string;
  promotions: App.BedbankPromotion[];
  isUpsell?: boolean;
}

function BedbankOfferTilePriceDetails(props: Props & MappedStateProps) {
  const {
    rate,
    rooms,
    offer,
    filters,
    offerUrl,
    suggestedDates,
    isUpsell,
  } = props

  const showNewTileDesign = useShowNewHotelSearch()
  const { t } = useTranslation(['common', 'offerList', 'hotelOfferList'])

  const { showTaxExclusivePrices } = useContext(GeoContext)
  const isFlexibleSearch = filters?.flexibleNights && suggestedDates?.checkIn && suggestedDates?.checkOut

  const taxesAndFees = rate?.totals?.taxesAndFees ?? 0
  const propertyFees = rate?.totals?.propertyFees ?? 0
  const hotelPrice = suggestedDates?.price ?? rate ? showTaxExclusivePrices ? rate.totals.exclusive : rate.totals.inclusive : offer.sell?.price
  const hotelValue = suggestedDates?.value ?? rate?.value ?? offer.sell?.value
  const hotelDiscount = rate?.discount ?? offer.sell.discount

  const hotelNights = rate?.nights ?? offer?.sell?.los

  const [flightPrice] = useFlightPrice(offer.flights?.airportCode, rooms, filters?.checkIn, filters?.checkOut, null, hotelNights)

  const exclusivePriceColor = useHotelPriceColour(taxesAndFees)
  const totalPrice = showTaxExclusivePrices ? hotelPrice : hotelPrice + propertyFees

  return <Root className={cn({ upsell: isUpsell, newTile: showNewTileDesign })}>
    <Caption variant={!isUpsell ? 'medium' : 'large'} colour="neutral-two">
      {isFlexibleSearch && buildSuggestedDatesStringTranslated(t, suggestedDates.checkIn, suggestedDates.checkOut)}
      {!isFlexibleSearch && t('totalForNights', { count: hotelNights, ns: 'common' })}
      {(rooms?.length ?? 0) > 0 && `, ${t('occupantsFrom', { ns: 'common', occupancy: formatOccupantsShortTranslated(t, rooms) })}`}
    </Caption>
    <InlineGroup vertical="baseline">
      <Heading variant="heading3" colour={exclusivePriceColor}>
        <FormatCurrency value={totalPrice} format="roundedDollar" />
      </Heading>
    </InlineGroup>
    {taxesAndFees > 0 && (<TaxesAndFees taxAmount={taxesAndFees} />)}
    {hotelDiscount > 0 &&
      <BodyText variant="small" weight="semi-bold" colour="neutral-one">
        <Trans i18nKey="Valued up to" ns="common">
          Valued up to <FormatCurrency value={hotelValue} format="roundedDollar" />
        </Trans>
        {hotelDiscount > config.displayDiscountThreshold.bedbank && !isUpsell &&
          <PaletteProvider type="default">
            <StyledPill kind="primary" variant="success">
              {t('percentageOff', { ns: 'common', discountPercent: Math.round(hotelDiscount * 100) })}
            </StyledPill>
          </PaletteProvider>
        }
      </BodyText>
    }

    {config.businessTraveller.currentAccountMode === 'business' && <BusinessTravellerOfferCreditsTextLink
      type="estimate"
      offer={offer}
      totalCost={totalPrice}
      numberOfNights={hotelNights}
    />}

    {isUpsell && <UpsellCTAButton kind="primary" fit={showNewTileDesign ? 'full-width' : 'auto'} to={offerUrl}>
      {t('View Offer', { ns: 'common' })}
    </UpsellCTAButton>}

    {rate && !isUpsell && (<PriceDetailsModal
      offer={offer}
      rooms={rooms}
      propertyFees={propertyFees}
      hotelPrice={hotelPrice}
      taxesAndFees={taxesAndFees}
      flightPrice={flightPrice}
      duration={hotelNights}
      labelSize="small"
      cta={
        <TextButton kind="primary" fit="flex" to={offerUrl}>
          {t('View Offer', { ns: 'common' })}
        </TextButton>
      }
    />)}
  </Root>
}

export default connect<MappedStateProps, undefined, Props, App.State>((appState, ownProps) => {
  const flexibleSearchFilterKey = buildSuggestedDatesParamsKey(ownProps.filters?.flexibleMonths, ownProps.filters?.flexibleNights, ownProps.filters?.rooms)
  return {
    suggestedDates: getSuggestedDates(appState, flexibleSearchFilterKey, ownProps.offer.id),
  }
})(BedbankOfferTilePriceDetails)
